import type { FunctionalComponent } from 'preact';
import { useRoute } from 'preact-iso';
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import { useRequest, formatLabel, formatDate, formatString, download } from '@ox/blox';
import { visitFormsApi } from '@ox/visits-ui/clients/mtx';
import type { MediaFile } from '@ox/ox-brokerage-client';
import '@ox/visits-ui/components/pages/ImageGalleryPage.scss';

/**
 * Thumbnail component
 */
interface ThumbnailProps {
  image: MediaFile;
  index: number;
  isActive: boolean;
  activeThumbnailRef: { current: HTMLDivElement | null } | null;
  onClick: (index: number) => void;
}

const Thumbnail: FunctionalComponent<ThumbnailProps> = ({
  image, index, isActive,
  activeThumbnailRef, onClick,
}) => {
  const handleClick = useCallback(() => {
    onClick(index);
  }, [index, onClick]);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick(index);
    }
  }, [index, onClick]);

  return (
    <div
      ref={isActive ? activeThumbnailRef : null}
      className={`image-gallery__thumbnail ${
        isActive ? 'image-gallery__thumbnail--active' : ''
      }`}
      role="button"
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={0}
      aria-label={`View image ${index + 1}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      <img
        src={image.signedUrl as string}
        alt={image.originalFilename}
        className="image-gallery__thumbnail-image"
      />
    </div>
  );
};

/**
 * Image Gallery Page component
 */
export const ImageGalleryPage: FunctionalComponent = () => {
  const { params } = useRoute();
  const { results, isPending, request } = useRequest({
    context: visitFormsApi,
    method: visitFormsApi.listPrewalkPhotosApiV1VisitFormsPrewalkPhotosVisitFormIdGet,
  });

  const [currentIndex, setCurrentIndex] = useState(0);
  const [imageLoading, setImageLoading] = useState(true);
  const [isZoomed, setIsZoomed] = useState(false);
  const [zoomPercent, setZoomPercent] = useState(100);
  const [isDraggingZoom, setIsDraggingZoom] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDownloadingStamped, setIsDownloadingStamped] = useState(false);
  const thumbnailStripRef = useRef<HTMLDivElement>(null);
  const activeThumbnailRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    request({ visitFormId: params.visitFormId as string });
  }, [request, params.visitFormId]);

  const handlePrevious = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setImageLoading(true);
      setZoomPercent(100);
    }
  }, [currentIndex]);

  const handleNext = useCallback(() => {
    if (results.data && currentIndex < results.data.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setImageLoading(true);
      setZoomPercent(100);
    }
  }, [currentIndex, results.data]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!results.data || results.data.length === 0) {
        return;
      }

      if (e.key === 'ArrowLeft') {
        handlePrevious();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, results.data, handleNext, handlePrevious]);

  // Auto-scroll to active thumbnail
  useEffect(() => {
    if (activeThumbnailRef.current && thumbnailStripRef.current) {
      activeThumbnailRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }
  }, [currentIndex]);

  const handleThumbnailClick = useCallback((index: number) => {
    setCurrentIndex(index);
    setImageLoading(true);
    setZoomPercent(100);
  }, []);

  const handleImageLoad = useCallback(() => {
    setImageLoading(false);
  }, []);

  const handleImageDoubleClick = useCallback(() => {
    setIsZoomed((prev) => !prev);
    setZoomPercent(100);
  }, []);

  const handleZoomChange = useCallback((e: Event) => {
    const target = e.target as HTMLInputElement | null;
    if (!target) {
      return;
    }
    const value = Number.parseInt(target.value, 10);
    if (Number.isNaN(value)) {
      return;
    }

    // Clamp 100..500 to be safe even if the DOM is manipulated
    setZoomPercent(Math.min(500, Math.max(100, value)));
  }, []);

  const handleZoomMouseDown = useCallback(() => {
    setIsDraggingZoom(true);
  }, []);

  const handleZoomMouseUp = useCallback(() => {
    setIsDraggingZoom(false);
  }, []);

  // Selection mode handlers
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) {
      return `${bytes} B`;
    }
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Download all photos as ZIP
  const handleDownloadAll = useCallback(async () => {
    if (!params.visitFormId) {
      return;
    }

    setIsDownloading(true);
    try {
      const response = await visitFormsApi.downloadPrewalkPhotosZipApiV1VisitFormsPrewalkPhotosZipVisitFormIdPostRaw(
        { visitFormId: params.visitFormId as string },
      );

      const blob = await response.raw.blob();
      download(blob, `prewalk_photos_${Date.now()}.zip`);
    } catch (error) {
      console.error('Download failed:', error);
      alert('Failed to download photos. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  }, [params.visitFormId]);

  // Download all photos as ZIP with timestamp overlays
  const handleDownloadAllStamped = useCallback(async () => {
    if (!params.visitFormId) {
      return;
    }

    setIsDownloadingStamped(true);
    try {
      const response = await fetch(
        `/api/v1/visit-forms/prewalk_photos/zip/${params.visitFormId}?stamped=true`,
        { method: 'POST' },
      );
      if (!response.ok) {
        throw new Error('Download failed');
      }
      const blob = await response.blob();
      download(blob, `prewalk_photos_${params.visitFormId}_timestamped.zip`);
    } catch (error) {
      console.error('Download failed:', error);
      alert('Failed to download timestamped photos. Please try again.');
    } finally {
      setIsDownloadingStamped(false);
    }
  }, [params.visitFormId]);

  // Derive a descriptive filename from mediaType + contentType extension,
  // e.g. "hvac_photo.jpg" instead of the raw original filename.
  const buildDownloadFilename = useCallback((imageData: MediaFile): string => {
    const ext = imageData.contentType.split('/')[1]?.split('+')[0] ?? 'bin';
    const slug = formatString(imageData.mediaType, { normalize: 'whitespace', outputCase: 'snake' });
    return `${slug}.${ext}`;
  }, []);

  // Download current photo using the backend download endpoint that returns a Content-Disposition signed URL
  const handleDownloadCurrent = useCallback(async () => {
    if (!results.data || results.data.length === 0) {
      return;
    }
    const imageData = results.data[currentIndex];
    try {
      const mediaFile = await visitFormsApi.getMediaDownloadUrlApiV1VisitFormsDownloadMediaMediaIdGet({
        mediaId: imageData.id,
      });
      await download(mediaFile.signedUrl as string, buildDownloadFilename(imageData));
    } catch (error) {
      console.error('Download failed:', error);
      alert('Failed to download photo. Please try again.');
    }
  }, [results.data, currentIndex, buildDownloadFilename]);

  // Don't render the gallery until we have data
  if (isPending || !results.data || results.data.length === 0) {
    return <div className="image-gallery-loading">Loading gallery...</div>;
  }

  const currentImage = results.data[currentIndex];
  const imageUrl = currentImage.signedUrl as string;
  const totalImages = results.data.length;

  return (
    <div className={`image-gallery ${isZoomed ? 'image-gallery--zoomed' : ''}`}>
      {/* Header */}
      {!isZoomed && (
        <div className="image-gallery__header">
          <h1 className="image-gallery__title">📷 Photos Gallery</h1>
          <div className="image-gallery__counter">
            {currentIndex + 1} / {totalImages}
          </div>
        </div>
      )}

      {/* Main Image Area */}
      <div className="image-gallery__main">
        {/* Previous Button */}
        <button
          className="image-gallery__nav-button image-gallery__nav-button--prev"
          onClick={handlePrevious}
          disabled={currentIndex === 0}
          aria-label="Previous image"
        >
          ‹
        </button>

        {/* Main Image */}
        <div className={`image-gallery__image-container ${isZoomed ? 'image-gallery__image-container--zoomed' : ''}`}>
          {imageLoading && (
            <div className="image-gallery__loader">
              <div className="image-gallery__spinner" />
            </div>
          )}
          <img
            src={imageUrl}
            alt={currentImage.originalFilename}
            className={`image-gallery__image ${isZoomed ? 'image-gallery__image--zoomed' : ''}`}
            onLoad={handleImageLoad}
            onClick={handleImageDoubleClick}
            style={{
              opacity: imageLoading ? 0 : 1,
              transform: isZoomed ? `scale(${zoomPercent / 100})` : undefined,
              transformOrigin: 'top left',
            }}
          />
        </div>

        {/* Next Button */}
        <button
          className="image-gallery__nav-button image-gallery__nav-button--next"
          onClick={handleNext}
          disabled={currentIndex === totalImages - 1}
          aria-label="Next image"
        >
          ›
        </button>
      </div>

      {/* Zoom Slider (shown only in zoom mode) */}
      {isZoomed && (
        <div className="image-gallery__zoom-controls" aria-label="Zoom controls">
          {isDraggingZoom && (
            <label className="image-gallery__zoom-label" htmlFor="image-gallery-zoom">
              Zoom: {zoomPercent}%
            </label>
          )}
          <input
            id="image-gallery-zoom"
            className="image-gallery__zoom-slider"
            type="range"
            min={100}
            max={500}
            step={5}
            value={zoomPercent}
            onInput={handleZoomChange}
            onMouseDown={handleZoomMouseDown}
            onMouseUp={handleZoomMouseUp}
            onTouchStart={handleZoomMouseDown}
            onTouchEnd={handleZoomMouseUp}
            aria-label="Zoom percentage"
          />
        </div>
      )}

      {/* Image Info */}
      {!isZoomed && (
        <div className="image-gallery__info">
          <div className="image-gallery__info-text">
            <div className="image-gallery__info-title">
              {formatLabel(currentImage.mediaType)}
            </div>
            <div className="image-gallery__info-meta">
              Uploaded: {formatDate(currentImage.createdAt)} •{' '}
              {formatFileSize(currentImage.fileSize)} •{' '}
              {currentImage.contentType.split('/')[1].toUpperCase()}
            </div>
          </div>
          <div className="image-gallery__info-actions">
            <button
              type="button"
              className="image-gallery__download-button"
              onClick={handleDownloadCurrent}
              aria-label="Download current photo"
            >
              ⬇ Download
            </button>
            <button
              type="button"
              className="image-gallery__download-button"
              onClick={handleDownloadAll}
              disabled={isDownloading || isDownloadingStamped}
              aria-label="Download all photos as ZIP"
            >
              {isDownloading ? '⏳ Downloading...' : '⬇ All'}
            </button>
            <button
              type="button"
              className="image-gallery__download-button"
              onClick={handleDownloadAllStamped}
              disabled={isDownloading || isDownloadingStamped}
              aria-label="Download all photos with timestamps as ZIP"
            >
              {isDownloadingStamped ? '⏳ Downloading...' : '⬇ All (timestamped)'}
            </button>
          </div>
        </div>
      )}

      {/* Thumbnail Strip */}
      {!isZoomed && (
        <div
          className="image-gallery__thumbnails"
          ref={thumbnailStripRef}
        >
          {results.data.map((image: MediaFile, index: number) => (
            <Thumbnail
              key={image.id}
              image={image}
              index={index}
              isActive={index === currentIndex}
              activeThumbnailRef={index === currentIndex ? activeThumbnailRef : null}
              onClick={handleThumbnailClick}
            />
          ))}
        </div>
      )}
    </div>
  );
};
